import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
// utils
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { defaultLang } from './config-lang';
//
import enLocales from './langs/en';
import deLocales from './langs/de';
import { customerOverrides } from './langs/customers';

// ----------------------------------------------------------------------

let lng = defaultLang.value;

const storageAvailable = localStorageAvailable();

if (storageAvailable) {
  lng = localStorage.getItem('i18nextLng') || defaultLang.value;
}

// function to combine the customer-specific language files with the default
// ones (e.g. "einrichtung-klient-de.ts" with "de.ts"). Executed only once
// during initialization of the application.
function deepMergeLanguageFiles(base: any, override: any): any {
  const result = { ...base };
  for (const key in override) {
    if (override[key] && typeof override[key] === 'object' && !Array.isArray(override[key])) {
      result[key] = deepMergeLanguageFiles(base[key] || {}, override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

// Fetch configuration and initialize i18n
async function initializeI18n() {
  let useCustomLabels = false;
  let customerLabelName = '';

  try {
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error('Failed to load config.json');
    }
    const config = await response.json();

    // Parse the config values (they come as strings from JSON)
    useCustomLabels = config.useCustomLabels === 'true' || config.useCustomLabels === true;
    customerLabelName = config.customerLabelName || '';

    if (useCustomLabels && customerLabelName) {
      console.info(`Using customer labels: '${customerLabelName}'`);
    } else {
      console.info('Using default labels');
    }
  } catch (error) {
    console.warn('Could not load config.json, using default labels', error);
  }

  // Get customer override if enabled and exists
  const customerOverride =
    useCustomLabels && customerLabelName && customerOverrides[customerLabelName]
      ? customerOverrides[customerLabelName]
      : null;

  if (useCustomLabels && customerLabelName && !customerOverride) {
    console.warn(
      `Customer override for '${customerLabelName}' not found. Using default translations.`
    );
  }

  // Merge translations with customer overrides if available
  const deTranslations = customerOverride
    ? deepMergeLanguageFiles(deLocales, customerOverride.de)
    : deLocales;

  const enTranslations = customerOverride
    ? deepMergeLanguageFiles(enLocales, customerOverride.en)
    : enLocales;

  // Initialize i18n with the merged translations
  // eslint-disable-next-line import/no-named-as-default-member
  return i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { translations: enTranslations },
        de: { translations: deTranslations },
      },
      lng,
      fallbackLng: defaultLang.value,
      debug: false,
      ns: ['translations'],
      defaultNS: 'translations',
      interpolation: {
        escapeValue: false,
      },
    });
}

initializeI18n().catch((error) => {
  console.error('Failed to initialize i18n:', error);
});

export default i18n;
